import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DropMessageTypeEnum, ReportTypeEnum } from '@piqet-angular/shared';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ViewportScroller } from '@angular/common';
import { customDecode, customEncode } from '@piqet-angular/data';
@Component({
  selector: 'piqet-angular-chart-at-glance',
  templateUrl: './chart-at-glance.component.html',
  styleUrls: ['./chart-at-glance.component.scss'],
})
export class ChartAtGlanceComponent {
  //properties
  reportChartForm!: FormGroup;
  productList: any;
  allowSelectScenario: number = 6;
  chartTypeList: any;
  comparisonByList: any;
  categoriesIncludeList: any;
  functionalUnitList: any;
  chartTypeItems: any;
  baseScenarioList: any;
  glanceResponseData: any;
  selectedProducts: any = [];
  selectedCategoryObjects: any = [];
  productConfigId!: number;
  showBaseLineComparison: boolean = false;
  selectedPEFType: string = 'Normalized';
  isChartNormalized: boolean = false;
  generatedChartType: string = 'column';
  reportTypeId: number = ReportTypeEnum.CustomContributionAnalysisHighLevel;
  impactCategoryId: number = 0;
  private readonly REPORT_FORMAT_ID = 2;
  readonly BAR_CHART_ID = 2;
  readonly SPIDER_CHART_ID = 3;
  private readonly CUSTOM_CATEGORY = 'Customize categories';
  private readonly PEF_WEIGHTED_ID = 21;
  private readonly PEF_NORMALIZED_ID = 22;
  chartData: any = [];
  pef_weighted: any = [];
  pef_normalized: any = [];
  opacity: any = 0.5;
  chartOpacity: any = { value: 0.5 };
  indicatorName: string = '';
  topIndicatorName: any = { value: 'Contribution by life cycle stages' };
  private readonly indicatorNameMap: any = {
    14: 'Contribution by life cycle stages',
    16: 'Contribution by packaging levels',
    18: 'Contribution by packaging components',
    17: 'Contribution by product waste',
    19: 'Contribution by ingredients',
    20: 'Contribution of transport',
  };

  comparisonAndCategoryLabel: string = 'Comparison by';

  isCategoryMultipleSelect: boolean = false;
  isPEFChart: boolean = false;
  showCategoryToInclude: boolean = true;
  showChartType: number = this.BAR_CHART_ID;

  parentId = null;
  isPEFResults = false;

  constructor(
    private readonly router: Router,
    private readonly sharedService: SharedService,
    private readonly reportService: ReportsService,
    private readonly fb: FormBuilder,
    private readonly viewportScroller: ViewportScroller
  ) {
    const url = location.href.split('/');
    this.productConfigId = Number(atob(decodeURIComponent(customDecode(url[6]))));
    this.sharedService.scenarioNameFunc('Chart at a glance');
    this.reportChartForm = this.fb.group({
      products: ['', [Validators.required]],
      chartType: [null, [Validators.required]],
      comparisonBy: [null, [Validators.required]],
      categoriesToInclude: [null, [Validators.required]],
      functionalUnit: [null, [Validators.required]],
      baselineFunctional: [null],
    });
    this.reportChartForm.get('functionalUnit')?.valueChanges.subscribe(value => {
      if (value?.id === 3) {
        this.showBaseLineComparison = true;
        this.baseScenarioList = this.selectedProducts;
        this.reportChartForm.get('baselineFunctional')?.setValidators(Validators.required);
      } else {
        this.showBaseLineComparison = false;
        this.reportChartForm.get('baselineFunctional')?.clearValidators();
        this.reportChartForm.get('baselineFunctional')?.setValue(null);
      }
      this.reportChartForm.get('baselineFunctional')?.updateValueAndValidity();
    });
    this.reportChartForm.get('chartType')?.valueChanges.subscribe(value => {
      if (value?.id === 2) {
        this.comparisonAndCategoryLabel = 'Comparison by';
        this.comparisonByList = value.childItems;
      } else {
        this.comparisonAndCategoryLabel = 'Select impact category';
        this.comparisonByList = value.childItems;
      }
      this.reportChartForm.get('comparisonBy')?.setValue(null);
      this.reportChartForm.get('comparisonBy')?.markAsUntouched();
    });
  }

  async ngOnInit(): Promise<void> {
    this.viewportScroller.scrollToPosition([0, 0]);
    await this.getGlanceReportData();
  }

  async getGlanceReportData() {
    const response = await this.reportService.getGlanceReportData(this.productConfigId).toPromise();
    if (response) {
      this.glanceResponseData = response.data;
      this.productList = response.data.selectedItemIds;
      this.categoriesIncludeList = response.data.indicatorItems;
      this.functionalUnitList = response.data.functionalUnitItems;
      this.chartTypeItems = response.data.chartTypeItems;

      const selectedProductIdStrs: string[] = [];
      const selectedProductObjects: any[] = [];

      for (let i = 0; i < this.productList.length; i++) {
        if (this.productList[i].selected === true) {
          selectedProductIdStrs.push(this.productList[i].idStr);
          selectedProductObjects.push(this.productList[i]);
        }
      }

      if (selectedProductIdStrs.length > 0) {
        this.reportChartForm.patchValue({
          products: selectedProductIdStrs,
        });

        this.selectedProducts = selectedProductObjects;
        this.baseScenarioList = selectedProductObjects;

        if (selectedProductObjects.length > 1) {
          this.chartTypeList = this.chartTypeItems;
        } else {
          this.chartTypeList = this.chartTypeItems.filter(
            (chart: any) => chart.description === 'Bar Chart'
          );
        }
      }

      // Pre-select chart type if marked as selected: true
      const selectedChartType = this.chartTypeItems.find((item: any) => item.selected === true);
      if (selectedChartType) {
        this.reportChartForm.patchValue({
          chartType: selectedChartType,
        });
        this.comparisonAndCategoryLabel =
          selectedChartType.id === 2 ? 'Comparison by' : 'Select impact category';
        this.comparisonByList = selectedChartType.childItems;

        // Pre-select comparison by if available in child items
        const selectedComparison = selectedChartType.childItems?.find(
          (item: any) => item.selected === true
        );
        if (selectedComparison) {
          this.reportChartForm.patchValue({
            comparisonBy: selectedComparison,
          });
          this.reportTypeId = selectedComparison.id;

          // Trigger comparison by change logic
          this.handleComparisonBySelection(selectedComparison, selectedChartType.id);
        }
      }

      // Pre-select functional unit if marked as selected: true
      const selectedFunctionalUnit = this.functionalUnitList.find(
        (item: any) => item.selected === true
      );
      if (selectedFunctionalUnit) {
        this.reportChartForm.patchValue({
          functionalUnit: selectedFunctionalUnit,
        });
      }

      // Pre-select categories to include AFTER determining selection mode
      // This needs to happen after chart type and comparison by are set
      this.preSelectCategories();
    }
  }

  // Helper method to pre-select categories based on selected flag
  private preSelectCategories() {
    const selectedCategoryIds: number[] = [];
    const selectedCategoryObjects: any[] = [];

    // Loop through and extract both IDs and full objects
    for (let i = 0; i < this.categoriesIncludeList.length; i++) {
      if (this.categoriesIncludeList[i].selected === true) {
        selectedCategoryIds.push(this.categoriesIncludeList[i].id);
        selectedCategoryObjects.push(this.categoriesIncludeList[i]);
      }
    }

    if (selectedCategoryIds.length > 0) {
      // Store full objects for later use
      this.selectedCategoryObjects = selectedCategoryObjects;

      // Use setTimeout to ensure ng-select is fully initialized
      setTimeout(() => {
        // Determine what to set based on selection mode
        if (this.isCategoryMultipleSelect) {
          // Multiple selection mode - set array of IDs
          this.reportChartForm.patchValue({
            categoriesToInclude: selectedCategoryIds,
          });
        } else {
          // Single selection mode - set first ID only
          this.reportChartForm.patchValue({
            categoriesToInclude: selectedCategoryIds[0],
          });
        }
      }, 0);
    }
  }

  private handleComparisonBySelection(comparisonBy: any, chartTypeId: number) {
    this.isChartNormalized = false;
    this.isPEFResults = false;
    this.isCategoryMultipleSelect = false;
    this.showCategoryToInclude = true;
    if (chartTypeId == this.SPIDER_CHART_ID && comparisonBy.name == this.CUSTOM_CATEGORY) {
      this.isCategoryMultipleSelect = true;
      this.reportChartForm.get('categoriesToInclude')?.setValidators(Validators.required);
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
    } else if (chartTypeId == this.SPIDER_CHART_ID && comparisonBy.name != this.CUSTOM_CATEGORY) {
      this.isCategoryMultipleSelect = false;
      this.showCategoryToInclude = false;
      this.reportChartForm.get('categoriesToInclude')?.clearValidators();
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
    }

    if (
      chartTypeId == this.BAR_CHART_ID &&
      (comparisonBy.id == this.PEF_WEIGHTED_ID || comparisonBy.id == this.PEF_NORMALIZED_ID)
    ) {
      this.reportChartForm.get('categoriesToInclude')?.clearValidators();
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
      this.showCategoryToInclude = false;
    } else if (
      chartTypeId == this.BAR_CHART_ID &&
      comparisonBy.id != this.PEF_WEIGHTED_ID &&
      comparisonBy.id != this.PEF_NORMALIZED_ID
    ) {
      this.reportChartForm.get('categoriesToInclude')?.setValidators(Validators.required);
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
    }
    this.generatedChartType = 'column';

    // Now that we've determined the selection mode, pre-select categories
    this.preSelectCategories();
  }

  async onProductChange(event: any) {
    if (!event.length) {
      this.selectedProducts = null;
      return;
    }

    // event contains the idStr values (due to bindValue="idStr")
    this.selectedProducts = [];
    for (let i = 0; i < event.length; i++) {
      const product = this.productList.find((p: any) => p.idStr === event[i]?.idStr);
      if (product) {
        this.selectedProducts.push(product);
      }
    }

    this.baseScenarioList = this.selectedProducts;
    if (event.length <= 1) {
      this.reportChartForm.get('chartType')?.setValue(null);
      this.reportChartForm.get('chartType')?.markAsUntouched();
    }
    let chartTypeList = [];
    if (this.selectedProducts.length > 1) {
      chartTypeList = this.chartTypeItems;
    } else {
      chartTypeList = this.chartTypeItems.filter((chart: any) => chart.description === 'Bar Chart');
    }
    this.chartTypeList = chartTypeList;
  }

  onComparisonByChange(event: any) {
    this.isChartNormalized = false;
    this.isPEFResults = false;
    this.isPEFChart = false;
    this.isCategoryMultipleSelect = false;
    this.showCategoryToInclude = true;
    const chartType = this.reportChartForm.get('chartType')?.value.id;
    if (chartType == this.SPIDER_CHART_ID && event.name == this.CUSTOM_CATEGORY) {
      this.isCategoryMultipleSelect = true;
      this.reportChartForm.get('categoriesToInclude')?.setValue(null);
      this.reportChartForm.get('categoriesToInclude')?.markAsUntouched();
      this.reportChartForm.get('categoriesToInclude')?.setValidators(Validators.required);
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
    } else if (chartType == this.SPIDER_CHART_ID && event.name != this.CUSTOM_CATEGORY) {
      this.isCategoryMultipleSelect = false;
      this.showCategoryToInclude = false;
      this.reportChartForm.get('categoriesToInclude')?.setValue(null);
      this.reportChartForm.get('categoriesToInclude')?.markAsUntouched();
      this.reportChartForm.get('categoriesToInclude')?.clearValidators();
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
    }
    if (
      chartType == this.BAR_CHART_ID &&
      (event.id == this.PEF_WEIGHTED_ID || event.id == this.PEF_NORMALIZED_ID)
    ) {
      this.reportChartForm.get('categoriesToInclude')?.setValue(null);
      this.reportChartForm.get('categoriesToInclude')?.markAsUntouched();
      this.reportChartForm.get('categoriesToInclude')?.clearValidators();
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
      this.showCategoryToInclude = false;
    } else if (
      chartType == this.BAR_CHART_ID &&
      event.id != this.PEF_WEIGHTED_ID &&
      event.id != this.PEF_NORMALIZED_ID
    ) {
      this.reportChartForm.get('categoriesToInclude')?.setValue(null);
      this.reportChartForm.get('categoriesToInclude')?.markAsUntouched();
      this.reportChartForm.get('categoriesToInclude')?.setValidators(Validators.required);
      this.reportChartForm.get('categoriesToInclude')?.updateValueAndValidity();
    }
    this.generatedChartType = 'column';
    this.reportTypeId = event?.id;
  }

  updateValidationForPEFCategories() {
    this.reportChartForm.controls['categoriesToInclude']?.clearValidators();
    this.reportChartForm.controls['categoriesToInclude'].updateValueAndValidity();
  }

  generateChart() {
    this.isPEFResults = false;

    const transformProducts = this.selectedProducts.map((x: any) => {
      return x.idStr;
    });
    if (this.reportChartForm.invalid) {
      this.reportChartForm.markAllAsTouched();
      return;
    }
    this.generatedChartType = 'column';
    const isSpiderChart = this.reportChartForm.get('chartType')?.value.id === this.SPIDER_CHART_ID;
    this.isPEFChart =
      (!isSpiderChart &&
        this.reportChartForm.get('comparisonBy')?.value.id === this.PEF_WEIGHTED_ID) ||
      this.reportChartForm.get('comparisonBy')?.value.id === this.PEF_NORMALIZED_ID;
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    const payload: any = {
      selectedItemIds: transformProducts.join(','),
      functionalUnitId: this.reportChartForm.value.functionalUnit.id,
      baseItemId: this.reportChartForm.value.baselineFunctional?.idStr ?? 0,
      reportTypeId: this.reportTypeId,
      projectId: this.productConfigId,
      charttypeId: this.reportChartForm.value.chartType.id ?? this.REPORT_FORMAT_ID,
    };

    const categories = this.reportChartForm.value.categoriesToInclude;
    if (Array.isArray(categories)) {
      payload['indicatorIds'] = categories.join(',');
    } else if (categories && typeof categories === 'number') {
      payload['indicatorIds'] = categories.toString();
    }

    this.reportService.generateCAGReport(payload).subscribe((res: any) => {
      if (res.messageType == DropMessageTypeEnum.Success) {
        this.opacity = res.data.opacity;
        this.chartOpacity = { value: this.opacity };
        this.chartData = [...res.data.componentItems];
        this.indicatorName = res.data.indicatorName;
        this.topIndicatorName = { value: this.getTopIndicatorName(res.data.reportTypeId) };
        if (this.isPEFChart) {
          this.pef_normalized = res.data.pef_normalized;
          this.pef_weighted = res.data.pef_weighted;
          this.changePEFCategories('Normalized');
        }
        if (this.reportChartForm.get('chartType')?.value.id === this.SPIDER_CHART_ID) {
          this.chartData = res.data.itemList;
        }
        this.showChartType =
          this.reportChartForm.controls['chartType']?.value.id || this.BAR_CHART_ID;
        this.isPEFResults = true;
      }
    });
  }

  changePEFCategories(type: string) {
    this.selectedPEFType = type;
    if (type === 'Weighted') {
      this.chartData = this.pef_weighted;
      this.topIndicatorName = { value: 'Contribution by PEF categories (Weighted)' };
      this.generatedChartType = 'bar';
      this.chartOpacity = { value: this.opacity };
      this.isChartNormalized = false;
    } else {
      this.chartData = this.pef_normalized;
      this.topIndicatorName = { value: 'Contribution by PEF categories (Normalized)' };
      this.generatedChartType = 'column';
      this.isChartNormalized = true;
      this.chartOpacity = { value: this.opacity };
    }
    this.isPEFResults = true;
  }

  getTopIndicatorName(indicatorId: number) {
    return this.indicatorNameMap[indicatorId] ?? '';
  }

  commonInputSearchName(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  commonInputSearchDescription(term: string, item: any) {
    return item.description.toLowerCase().includes(term.toLowerCase());
  }

  compareFn(item1: any, item2: any): boolean {
    return item1 && item2 ? item1.id === item2.id : item1 === item2;
  }

  nextRoute() {
    const activeProductProjectId: any = localStorage.getItem('productConfigId');
    this.router.navigate(
      [
        `projects/product-configuration/${customEncode(
          btoa(activeProductProjectId)
        )}/report-generation`,
      ],
      {
        state: { isFromNext: true },
      }
    );
  }
}
